import { defineStore } from "pinia";
import api from "@/services/api";
import { applyFullAuthSession } from "@/utils/authSession";
import {
  clearPendingVerification,
  getPendingVerifyToken,
  getResendCooldownSeconds,
  getOtpExpirySeconds,
  updateVerifySessionFromResend,
} from "@/utils/pendingVerification";

function getLang() {
  const stored = localStorage.getItem("app_lang");
  const htmlLang = document.documentElement.lang;
  const lang = (stored || htmlLang || "ar").toLowerCase();
  return lang.startsWith("en") ? "en" : "ar";
}

function fallbackMsg() {
  return getLang() === "en"
    ? "Something went wrong. Please try again."
    : "حدث خطأ غير متوقع. حاول مرة أخرى.";
}

function pickMessage(payload) {
  if (!payload) return fallbackMsg();
  if (typeof payload === "string") return payload;

  return (
    payload.message ||
    payload.error ||
    payload.detail ||
    payload.msg ||
    (Array.isArray(payload.messages) ? payload.messages?.[0] : null) ||
    fallbackMsg()
  );
}

function normalizeErrors(payload) {
  if (!payload) return { message: fallbackMsg() };
  if (typeof payload === "string") return { message: payload };

  const fieldErrors = payload.errors || payload.data?.errors || {};
  const message = pickMessage(payload);

  return { message, ...fieldErrors };
}

function safeRetryAfter(err) {
  const body = err?.response?.data;
  const raBody = Number(body?.retry_after);
  const raHeader = Number(err?.response?.headers?.["retry-after"]);
  if (Number.isFinite(raBody) && raBody > 0) return raBody;
  if (Number.isFinite(raHeader) && raHeader > 0) return raHeader;
  return null;
}

function toIntSeconds(val, fallback = 60) {
  const n = Number(val);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.max(1, Math.ceil(n));
}

function authHeaders() {
  const token = getPendingVerifyToken();
  return {
    "Accept-Language": getLang(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export const useVerifyAccountStore = defineStore("verifyAccount", {
  state: () => ({
    loading: false,
    resendLoading: false,
    resendTimer: 0,
    otpExpiryTimer: null,
    _resendTimerId: null,
    _otpTimerId: null,
    errors: {},
  }),

  getters: {
    canResend: (s) => s.resendTimer <= 0 && !s.resendLoading && !s.loading,
    otpExpired: (s) => s.otpExpiryTimer !== null && s.otpExpiryTimer <= 0,
  },

  actions: {
    clearErrors() {
      this.errors = {};
    },

    _stopResendTimer() {
      if (this._resendTimerId) clearInterval(this._resendTimerId);
      this._resendTimerId = null;
      this.resendTimer = 0;
    },

    _stopOtpTimer() {
      if (this._otpTimerId) clearInterval(this._otpTimerId);
      this._otpTimerId = null;
      this.otpExpiryTimer = 0;
    },

    startResendTimer(seconds = 60) {
      this._stopResendTimer();

      this.resendTimer = toIntSeconds(seconds, 60);
      if (this.resendTimer <= 0) return;

      this._resendTimerId = setInterval(() => {
        this.resendTimer = Math.max(0, this.resendTimer - 1);
        if (this.resendTimer <= 0) this._stopResendTimer();
      }, 1000);
    },

    startOtpExpiryTimer(seconds) {
      this._stopOtpTimer();

      if (seconds == null) {
        this.otpExpiryTimer = null;
        return;
      }

      this.otpExpiryTimer = toIntSeconds(seconds, 0);
      if (this.otpExpiryTimer <= 0) return;

      this._otpTimerId = setInterval(() => {
        this.otpExpiryTimer = Math.max(0, this.otpExpiryTimer - 1);
        if (this.otpExpiryTimer <= 0) this._stopOtpTimer();
      }, 1000);
    },

    bootstrapTimers() {
      const resendRemaining = getResendCooldownSeconds();
      if (resendRemaining > 0) this.startResendTimer(resendRemaining);

      const otpRemaining = getOtpExpirySeconds();
      if (otpRemaining != null) this.startOtpExpiryTimer(otpRemaining);
    },

    async verifyOtp(otpCode) {
      this.loading = true;
      this.clearErrors();

      try {
        const payload = {
          otp_code: otpCode,
          token_name: "web",
        };

        const res = await api.post("/account-verify", payload, {
          headers: authHeaders(),
        });

        clearPendingVerification();
        this._stopResendTimer();
        this._stopOtpTimer();

        applyFullAuthSession(res.data);

        return res.data;
      } catch (err) {
        this.errors = normalizeErrors(err?.response?.data);
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async resendCode() {
      if (!this.canResend) return;

      this.resendLoading = true;
      this.clearErrors();

      try {
        const res = await api.post(
          "/account-verify/resend",
          {},
          { headers: authHeaders() }
        );

        updateVerifySessionFromResend(res?.data || {});

        const retryAfter = res?.data?.retry_after ?? 60;
        this.startResendTimer(retryAfter);

        const otpRemaining = getOtpExpirySeconds();
        if (otpRemaining != null) this.startOtpExpiryTimer(otpRemaining);

        return res.data;
      } catch (err) {
        const status = err?.response?.status;

        if (status === 429) {
          const ra = safeRetryAfter(err) ?? 60;
          updateVerifySessionFromResend(err?.response?.data || { retry_after: ra });
          this.startResendTimer(ra);
        }

        this.errors = normalizeErrors(err?.response?.data);
        throw err;
      } finally {
        this.resendLoading = false;
      }
    },

    reset() {
      this._stopResendTimer();
      this._stopOtpTimer();
      this.clearErrors();
      this.loading = false;
      this.resendLoading = false;
      this.otpExpiryTimer = null;
    },
  },
});
